import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  fetchCoreStatus,
  ingestReceipt,
  settleTransaction,
  receiptToToken,
} from '../lib/coreTap.js'

const DEMO_POS = [
  {
    merchant_name: 'Ridgefield Eye Associates',
    merchant_address: '24 Catoonah St, Ridgefield CT 06877',
    date: new Date().toISOString().split('T')[0], total: 142.50, tax: 11.38, persona: 'FAMILY',
    items: [
      { sku:'EXAM-001', description:'Comprehensive Eye Exam',      quantity:1, unit_price:95.00,  total_price:95.00  },
      { sku:'CLT-090',  description:'Contact Lens Trial Pack',     quantity:1, unit_price:22.00,  total_price:22.00  },
      { sku:'DRP-200',  description:'Preservative-Free Eye Drops', quantity:2, unit_price:12.75,  total_price:25.50  },
    ],
  },
  {
    merchant_name: 'Home Depot — Norwich CT',
    merchant_address: '311 Salem Turnpike, Norwich CT 06360',
    date: new Date().toISOString().split('T')[0], total: 847.23, tax: 63.82, persona: 'REAL_ESTATE',
    items: [
      { sku:'206145099', description:'2x6x8 Pressure-Treated Lumber',  quantity:24, unit_price:8.47,  total_price:203.28 },
      { sku:'100650953', description:'Simpson Strong-Tie Joist Hanger', quantity:50, unit_price:1.98,  total_price:99.00  },
      { sku:'204276445', description:'5/4x6x16 Premium Deck Board',     quantity:18, unit_price:22.97, total_price:413.46 },
      { sku:'204317968', description:'Deck Screw 5lb Box #10',          quantity:2,  unit_price:23.98, total_price:47.96  },
    ],
  },
  {
    merchant_name: 'Sony Style — Flagship', merchant_address: '550 Madison Ave, New York NY',
    date: new Date().toISOString().split('T')[0], total: 2999.99, tax: 265.75, persona: 'ROBOTICS',
    items: [
      { sku:'ERS-1000XB1',  description:'AIBO Entertainment Robot (Black)',    quantity:1, unit_price:2799.99, total_price:2799.99 },
      { sku:'AIBO-CLOUD-1', description:'AIBO Basic Plan 1-Year Subscription', quantity:1, unit_price:120.00,  total_price:120.00  },
      { sku:'AIBO-PAWS',    description:'AIBO Replacement Paw Set',            quantity:1, unit_price:79.99,   total_price:79.99   },
    ],
  },
]

function computeFee(total) {
  const c = Math.round(total * 100)
  if (c <= 1000) return { cents: 25, label: 'flat $0.25' }
  if (c < 10000) return { cents: 50, label: 'flat $0.50' }
  return { cents: Math.min(Math.round(c * 0.01), 2500), label: '1%' }
}

function modeLabel(mode) {
  if (mode === 'live') return { text: 'Live', className: 'bg-[rgba(0,255,135,0.12)] text-[#00ff87] border-[rgba(0,255,135,0.25)]' }
  if (mode === 'sandbox') return { text: 'Sandbox', className: 'bg-[rgba(245,183,49,0.12)] text-[#f5b731] border-[rgba(245,183,49,0.2)]' }
  return { text: 'Offline', className: 'bg-[rgba(255,255,255,0.06)] text-white/40 border-white/10' }
}

function NFCRing({ active }) {
  return (
    <motion.div className="relative w-[140px] h-[140px] flex items-center justify-center">
      {[0,1,2].map(i => (
        <div key={i} className={`absolute rounded-full border border-[#00ff87] nfc-ring-anim ${active ? '[animation-duration:0.7s]' : ''}`}
          style={{ width:`${100-(i*26)}%`, height:`${100-(i*26)}%`, animationDelay:`${i*0.4}s`,
            opacity: 0, boxShadow: active ? '0 0 20px rgba(0,255,135,0.3)' : 'none' }} />
      ))}
      <div className={`w-14 h-14 rounded-full flex items-center justify-center relative z-10 border transition-all duration-300
        ${active ? 'bg-[rgba(0,255,135,0.25)] border-[rgba(0,255,135,0.5)] shadow-[0_0_60px_rgba(0,255,135,0.4)]'
                 : 'bg-[rgba(0,255,135,0.08)] border-[rgba(0,255,135,0.2)]'}`}>
        <svg className="w-6 h-6 text-[#00ff87]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M6.5 6.5a8 8 0 0 1 11 11M3 3a13 13 0 0 1 18 18M2 12h.01M12 2v.01"/>
        </svg>
      </div>
    </motion.div>
  )
}

export default function NFCCapture({ navigate, setPending, pending, addToken, onSettled, userId }) {
  const [phase, setPhase]         = useState('idle')
  const [receipt, setReceipt]     = useState(null)
  const [enhanced, setEnhanced]   = useState(false)
  const [token, setToken]         = useState(null)
  const [pendingTxId, setPendingTxId] = useState(null)
  const [settleError, setSettleError] = useState(null)
  const [coreMode, setCoreMode]   = useState('offline')
  const demoIdx = useRef(0)

  useEffect(() => {
    fetchCoreStatus()
      .then((s) => setCoreMode(s.mode || (s.configured ? 'sandbox' : 'offline')))
      .catch(() => setCoreMode('offline'))
  }, [])

  const badge = modeLabel(coreMode)

  function suck(idx) {
    setSettleError(null)
    setPendingTxId(null)
    setPhase('sucking')
    setTimeout(() => {
      const r = DEMO_POS[idx !== undefined ? idx : (demoIdx.current++ % DEMO_POS.length)]
      setReceipt(r)
      setPhase('preview')
      if (setPending) setPending(r)
    }, 1600)
  }

  async function enhance() {
    setEnhanced(true)
    try {
      const res = await fetch('/api/enhance', {
        method: 'POST', headers: {'Content-Type':'application/json'},
        body: JSON.stringify({ items: receipt.items }),
      })
      if (res.ok) {
        const { items } = await res.json()
        setReceipt(r => ({ ...r, items }))
      }
    } catch { /* offline — keep original */ }
  }

  async function settle() {
    if (!receipt) return
    setSettleError(null)
    setPhase('settling')

    try {
      let txId = pendingTxId
      if (!txId) {
        const ing = await ingestReceipt(receipt)
        txId = ing.transaction_id
        setPendingTxId(txId)
      }

      const result = await settleTransaction(txId, receipt)
      const tot = receiptToToken(receipt, result)
    addToken(tot)
    setToken(tot)
    if (setPending) setPending(null)
    onSettled?.()
    setPhase('done')
    } catch (e) {
      setSettleError(e.message || 'Settlement failed')
      setPhase('preview')
    }
  }

  function reset() {
    setPhase('idle')
    setReceipt(null)
    setEnhanced(false)
    setToken(null)
    setPendingTxId(null)
    setSettleError(null)
    if (setPending) setPending(null)
  }

  const fee = receipt ? computeFee(receipt.total) : null

  return (
    <div className="min-h-full bg-[#080c10]">
      <motion.div
        className="sticky top-0 z-10 flex items-center justify-between px-5 pt-5 pb-0 bg-gradient-to-b from-[#080c10] to-transparent"
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        <div className="text-2xl font-black tracking-tight text-[#00ff87]" style={{textShadow:'0 0 20px rgba(0,255,135,0.4)'}}>
          itemiz<span className="text-white/40 font-normal">.it</span>
        </div>
        <motion.div
          key={badge.text}
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className={`text-[10px] font-bold tracking-widest uppercase px-3 py-1 rounded-full border ${badge.className}`}
        >
          {badge.text}
        </motion.div>
      </motion.div>

      <AnimatePresence mode="wait">

        {phase === 'idle' && (
          <motion.div key="idle" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} className="flex flex-col items-center px-5 pt-6">
            <div className="w-full glass-card p-5 mb-4">
              <p className="text-[11px] font-bold tracking-widest uppercase text-white/30 mb-1">TAP 1 — SUCK</p>
              <h2 className="text-xl font-black mb-1">Capture Receipt</h2>
              <p className="text-sm text-white/40">Hold near a POS terminal or pick a demo merchant below.</p>
            </div>

            <div className="flex flex-col items-center py-6 cursor-pointer" onClick={() => suck()}>
              <NFCRing active={false} />
              <p className="mt-4 text-xs font-semibold tracking-widest uppercase text-white/30">Hold near terminal</p>
            </div>

            <p className="text-[10px] font-bold tracking-widest uppercase text-white/20 mb-3">Simulate POS Terminal</p>
            <motion.div
              className="flex gap-2 w-full mb-5 overflow-x-auto pb-1"
              style={{scrollbarWidth:'none'}}
              initial="hidden"
              animate="visible"
              variants={{ visible: { transition: { staggerChildren: 0.06 } } }}
            >
              {[['👁','Eye Clinic',0],['🏠','Home Depot',1],['🤖','Sony AIBO',2]].map(([icon,label,idx]) => (
                <motion.button
                  key={idx}
                  variants={{ hidden: { opacity: 0, y: 8 }, visible: { opacity: 1, y: 0 } }}
                  onClick={() => suck(idx)}
                  className="flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-xl glass-card text-sm font-semibold text-white/60 hover:text-white transition-colors active:scale-95"
                >
                  {icon} {label}
                </motion.button>
              ))}
            </motion.div>
          </motion.div>
        )}

        {phase === 'sucking' && (
          <motion.div key="sucking" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
            className="flex flex-col items-center justify-center min-h-[60vh] px-5">
            <NFCRing active={true} />
            <motion.p className="mt-5 text-sm font-semibold text-white/50 tracking-wider"
              animate={{opacity:[0.4,1,0.4]}} transition={{duration:1.2,repeat:Infinity}}>
              Sucking receipt from terminal…
            </motion.p>
          </motion.div>
        )}

        {phase === 'preview' && receipt && (
          <motion.div key="preview" initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} exit={{opacity:0}} className="px-5 pt-5">
            <motion.div className="glass-card p-5 mb-3" layout>
              <motion.div className="text-xl font-black mb-0.5" layout>{receipt.merchant_name}</motion.div>
              <motion.div className="text-xs text-white/30" layout>{receipt.merchant_address} · {receipt.date}</motion.div>
              <motion.div
                className="mt-4 text-[44px] font-black tracking-tight text-[#00ff87]"
                style={{textShadow:'0 0 30px rgba(0,255,135,0.35)'}}
                layout
              >
                ${receipt.total.toFixed(2)}
              </motion.div>
              <motion.div className="text-xs text-white/30 mt-1" layout>
                Tax ${receipt.tax.toFixed(2)} · itemiz.it fee <span className="text-[#f5b731] font-semibold">${(fee.cents/100).toFixed(2)} ({fee.label})</span>
              </motion.div>
            </motion.div>

            <motion.div className="glass-card p-0 mb-3 overflow-hidden" layout>
              <motion.div className="px-4 py-3 border-b border-white/[0.06] flex items-center justify-between" layout>
                <span className="text-[11px] font-bold tracking-widest uppercase text-white/30">
                  {receipt.items.length} Items Captured
                </span>
                {!enhanced && (
                  <button onClick={enhance}
                    className="text-[11px] font-bold text-[#00ff87] flex items-center gap-1 hover:opacity-80 transition-opacity">
                    ✨ Magic Enhance
                  </button>
                )}
                {enhanced && <span className="text-[11px] text-[#00ff87] font-bold">✨ Enhanced</span>}
              </motion.div>
              {receipt.items.map((item, i) => (
                <motion.div
                  key={`${item.sku}-${i}`}
                  layout
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.04 }}
                  className={`flex items-start justify-between gap-3 px-4 py-3 ${i < receipt.items.length-1 ? 'border-b border-white/[0.04]' : ''}`}
                >
                  <motion.div className="flex-1 min-w-0" layout>
                    <div className="text-sm font-semibold leading-snug">{item.description}</div>
                    <motion.div className="sku-mono" layout>SKU {item.sku || '—'} · qty {item.quantity} · @${item.unit_price.toFixed(2)}/ea</motion.div>
                  </motion.div>
                  <motion.div className="text-[15px] font-bold text-[#00ff87] flex-shrink-0" layout>${item.total_price.toFixed(2)}</motion.div>
                </motion.div>
              ))}
            </motion.div>

            {settleError && (
              <p className="text-sm text-red-400/90 mb-3 text-center">{settleError}</p>
            )}

            <button onClick={settle} className="btn-green mb-2">💸 TAP 2 — Settle via FedNow</button>
            <button onClick={reset}  className="btn-ghost">Discard</button>
            <motion.div className="h-4" layout />
          </motion.div>
        )}

        {phase === 'settling' && (
          <motion.div key="settling" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
            className="flex flex-col items-center justify-center min-h-[60vh] px-5 gap-4">
            <div className="w-16 h-16 rounded-full bg-[rgba(0,255,135,0.12)] border border-[rgba(0,255,135,0.3)] flex items-center justify-center"
              style={{boxShadow:'0 0 40px rgba(0,255,135,0.3)'}}>
              <motion.div className="w-6 h-6 border-2 border-[#00ff87] border-t-transparent rounded-full"
                animate={{rotate:360}} transition={{duration:0.8,repeat:Infinity,ease:'linear'}}/>
            </div>
            <p className="text-sm font-semibold text-white/40">Locking ledger · FedNow settlement…</p>
          </motion.div>
        )}

        {phase === 'done' && token && (
          <motion.div key="done" initial={{opacity:0,scale:0.95}} animate={{opacity:1,scale:1}} exit={{opacity:0}} className="px-5 pt-5">
            <div className="flex flex-col items-center text-center py-6"
              style={{background:'linear-gradient(180deg,rgba(0,255,135,0.07) 0%,transparent 100%)'}}>
              <motion.div className="text-5xl mb-3" initial={{scale:0,rotate:-20}} animate={{scale:1,rotate:0}}
                transition={{type:'spring',stiffness:300,damping:20}}>✦</motion.div>
              <h2 className="text-2xl font-black">Token of Truth</h2>
              <p className="text-sm text-white/30 mt-1">Immutable · FedNow Verified</p>
            </div>

            <div className="glass-card p-5 mb-3">
              <motion.div className="text-lg font-black mb-1" layout>{token.merchant}</motion.div>
              <motion.div
                className="text-4xl font-black text-[#00ff87] mb-3"
                style={{textShadow:'0 0 30px rgba(0,255,135,0.35)'}}
                layout
              >
                ${parseFloat(token.total).toFixed(2)}
              </motion.div>
              <span className="tot-badge">VERIFIED_TRUTH ✓</span>
            </div>

            <div className="glass-card p-0 mb-3 overflow-hidden">
              <div className="px-4 py-3 border-b border-white/[0.06]">
                <span className="text-[11px] font-bold tracking-widest uppercase text-white/30">{token.items.length} Line Items Locked</span>
              </div>
              {token.items.map((item, i) => (
                <motion.div
                  key={i}
                  layout
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: i * 0.03 }}
                  className={`flex items-start justify-between gap-3 px-4 py-3 ${i < token.items.length-1 ? 'border-b border-white/[0.04]' : ''}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold">{item.description}</div>
                    <div className="sku-mono">SKU {item.sku}</div>
                  </div>
                  <motion.div className="text-[15px] font-bold text-[#00ff87]" layout>${item.total.toFixed(2)}</motion.div>
                </motion.div>
              ))}
            </div>

            <div className="glass-card p-4 mb-3">
              <div className="detail-row"><span className="text-white/40 text-sm">TX ID</span><span className="font-mono text-xs text-right break-all">{token.id}</span></div>
              <motion.div className="detail-row" layout><span className="text-white/40 text-sm">Bank TX</span><span className="font-mono text-xs text-[#00ff87] text-right break-all">{token.bankTxId}</span></motion.div>
              {token.receiptId && (
                <div className="detail-row"><span className="text-white/40 text-sm">Receipt</span><span className="font-mono text-xs text-right">{token.receiptId}</span></div>
              )}
              {token.auditSignature && (
                <div className="detail-row"><span className="text-white/40 text-sm">KMS Audit</span><span className="font-mono text-[10px] text-right truncate max-w-[200px]">{token.auditSignature.slice(0, 28)}…</span></div>
              )}
              {userId && (
                <div className="detail-row"><span className="text-white/40 text-sm">User</span><span className="font-mono text-[10px] text-right">{userId}</span></div>
              )}
            </div>

            {token.proofUrl && (
              <a href={token.proofUrl} target="_blank" rel="noopener noreferrer" className="btn-ghost block text-center mb-2 text-[#00ff87]">
                View proof of truth
              </a>
            )}

            <button onClick={reset}        className="btn-green mb-2">✓ Done</button>
            <button onClick={() => navigate('ledger')} className="btn-ghost">View Ledger</button>
            <div className="h-4"/>
          </motion.div>
        )}

      </AnimatePresence>
    </div>
  )
}
